import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import TimelineEvent from './TimelineEvent';

const TimelineRow = ({ dock, reservations, pxPerMin, visibleStart, visibleDurationMins, onEventClick, onEventContextMenu, canEdit = true }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `dock-${dock.id}`,
        data: { type: 'dock', dock }
    });

    // Grid Lines
    const lines = [];
    // Render lines every 15 minutes
    for (let i = 0; i <= visibleDurationMins / 15; i++) {
        const isHour = (i * 15) % 60 === 0;
        lines.push(
            <div
                key={i}
                className={`grid-line ${isHour ? 'grid-line-hour' : 'grid-line-quarter'}`}
                style={{
                    left: i * 15 * pxPerMin,
                }}
            />
        );
    }

    // Filter events for this dock
    const myEvents = reservations.filter(r => r.dockId === dock.id);

    // Process Overlaps to calculate visual layout properties (laneIndex, totalLanes)
    const processOverlaps = (events) => {
        if (events.length === 0) return [];

        // 1. Sort by start time
        const sorted = [...events].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

        // 2. Group into clusters (sets of mutually overlapping events)
        const clusters = [];
        let currentCluster = [];
        let clusterEnd = 0;

        for (const evt of sorted) {
            const start = new Date(evt.startTime).getTime();
            const end = new Date(evt.endTime).getTime();

            if (currentCluster.length === 0) {
                currentCluster.push(evt);
                clusterEnd = end;
            } else {
                // Use 1-second tolerance to prevent touching events from overlapping due to noise
                if (start < (clusterEnd - 1000)) {
                    // Overlap with the cluster timeframe
                    currentCluster.push(evt);
                    if (end > clusterEnd) clusterEnd = end;
                } else {
                    // Disjoint
                    clusters.push(currentCluster);
                    currentCluster = [evt];
                    clusterEnd = end;
                }
            }
        }
        if (currentCluster.length > 0) clusters.push(currentCluster);

        // 3. Assign lanes within clusters
        const result = [];

        clusters.forEach(cluster => {
            const lanes = []; // Track end time of the last event in each lane

            // We need to re-sort or just process. 
            // Simple lane packing: First Fit
            cluster.forEach(evt => {
                let placed = false;
                const start = new Date(evt.startTime).getTime();
                const end = new Date(evt.endTime).getTime();

                // Try to find an existing lane that is free
                for (let i = 0; i < lanes.length; i++) {
                    // Use 1-second tolerance here too
                    if (start >= (lanes[i] - 1000)) {
                        lanes[i] = end; // Update lane end time
                        evt.laneIndex = i;
                        placed = true;
                        break;
                    }
                }

                if (!placed) {
                    lanes.push(end);
                    evt.laneIndex = lanes.length - 1;
                }
            });

            const maxLanes = lanes.length;
            cluster.forEach(evt => {
                evt.totalLanes = maxLanes;
                result.push(evt);
            });
        });

        return result;
    };

    const processedEvents = processOverlaps(myEvents);

    return (
        <div className="timeline-row" style={{ display: 'flex', width: '100%' }}>
            {/* Sidebar */}
            <div className="row-sidebar flex flex-col items-center justify-center text-center gap-0.5" style={{
                width: '80px',
                minWidth: '80px',
                flexShrink: 0,
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
            }}>
                <div className="text-sm font-bold leading-tight">{dock.name}</div>
                <div className="text-[9px] opacity-60 font-medium uppercase tracking-tighter leading-none" style={{ color: 'var(--primary-color)' }}>
                    {dock.type}
                </div>
                {dock.status === 'maintenance' && (
                    <div className="text-red-400 text-[9px] font-bold leading-none mt-1">(BAKIMDA)</div>
                )}
            </div>

            {/* Droppable Content Area */}
            <div
                ref={setNodeRef}
                id={`dock-${dock.id}`}
                className="row-content"
                style={{
                    flex: 1,
                    position: 'relative',
                    background: isOver ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                    transition: 'background 0.2s'
                }}
            >
                {/* Grid Lines */}
                {lines}

                {/* Events */}
                {processedEvents.map(res => (
                    <TimelineEvent
                        key={res.id}
                        reservation={res}
                        pxPerMin={pxPerMin}
                        visibleStart={visibleStart}
                        onClick={onEventClick}
                        onContextMenu={onEventContextMenu}
                        laneIndex={res.laneIndex}
                        totalLanes={res.totalLanes}
                        isMasked={res.isMasked}
                        canDrag={canEdit && (['pending', 'pending_approval', 'waiting_reservation'].includes(res.status))}
                    />
                ))}
            </div>
        </div>
    );
};

export default TimelineRow;
